import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { FormGroup, FormArray, FormBuilder, FormControl } from '@angular/forms';
import { UserGroupRule } from '../../../models/feature-flag.model';

/**
 * Component for building and managing user group-based targeting rules
 */
@Component({
  selector: 'cbre-user-group-rule',
  templateUrl: './user-group-rule.component.html',
  styleUrls: ['./user-group-rule.component.scss'],
  standalone: false,
})
export class UserGroupRuleComponent implements OnInit {
  /**
   * Parent form group
   */
  @Input() parentForm: FormGroup;

  /**
   * Event emitted when the rule changes
   */
  @Output() ruleChanged = new EventEmitter<UserGroupRule>();

  /**
   * New user group input
   */
  newUserGroup = '';

  /**
   * Form control for the new user group input
   */
  newUserGroupControl = new FormControl('');

  /**
   * Error message for validation
   */
  error: string = null;

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    // Ensure the rule form group has a values array
    const ruleGroup = this.parentForm?.get('rule') as FormGroup;
    if (ruleGroup && !ruleGroup.contains('values')) {
      ruleGroup.addControl('values', this.fb.array([]));
    }
  }

  /**
   * Get the values form array
   */
  get valuesArray(): FormArray {
    const formArray = this.parentForm?.get('rule.values') as FormArray;
    return formArray || this.fb.array([]);
  }

  /**
   * Get the user groups as string array
   */
  get userGroups(): string[] {
    return this.valuesArray.value;
  }

  /**
   * Add a new user group
   */
  addUserGroup(): void {
    const newUserGroup = this.newUserGroupControl.value?.trim?.();
    if (!newUserGroup) {
      return;
    }

    // Check for duplicates
    if (this.userGroups.includes(newUserGroup)) {
      this.error = 'This user group is already in the list';
      return;
    }

    // Clear any error
    this.error = null;

    // Add to form array
    this.valuesArray.push(this.fb.control(newUserGroup));

    // Clear input
    this.newUserGroupControl.reset();

    // Emit the updated rule
    const updatedRule: UserGroupRule = {
      type: 'userGroup',
      values: this.userGroups,
    };
    this.ruleChanged.emit(updatedRule);
  }

  /**
   * Remove a user group
   */
  removeUserGroup(index: number): void {
    this.valuesArray.removeAt(index);

    // Emit the updated rule
    const updatedRule: UserGroupRule = {
      type: 'userGroup',
      values: this.userGroups,
    };
    this.ruleChanged.emit(updatedRule);
  }

  /**
   * Clear all user groups
   */
  clearUserGroups(): void {
    while (this.valuesArray.length) {
      this.valuesArray.removeAt(0);
    }

    // Emit the updated rule
    const updatedRule: UserGroupRule = {
      type: 'userGroup',
      values: [],
    };
    this.ruleChanged.emit(updatedRule);
  }

  /**
   * Handle Enter key in the input field
   */
  onInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.addUserGroup();
    }
  }
}
